import React, { useState } from 'react';
import { Modal, Input, Button, message, Card } from 'antd';
import { useSnapshot } from 'valtio';
import { CustomTemplateStore } from '@/utils/store/customTemplate';
import { EXAMPLE_TEMPLATES_LIST } from '@/constants/exampleTemplates';
import { ApiService } from '@/services/api';
import { handleError } from '@/utils/helpers';

const { TextArea } = Input;

interface AddTemplateModalProps {
  visible: boolean;
  onClose: () => void;
  userId: string;
}

const AddTemplateModal: React.FC<AddTemplateModalProps> = ({ visible, onClose, userId }) => {
  const [title, setTitle] = useState('');
  const [info, setInfo] = useState('');
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [isFileGenerating, setIsFileGenerating] = useState(false);
  const { addLoading } = useSnapshot(CustomTemplateStore);

  const currentTemplate = EXAMPLE_TEMPLATES_LIST[currentTemplateIndex];

  const handleSubmit = async () => {
    if (!title.trim()) {
      message.warning('请输入模板标题');
      return;
    }
    
    if (!info.trim()) {
      message.warning('请输入纪要大纲');
      return;
    }

    const success = await CustomTemplateStore.addTemplate({
      title: title.trim(),
      info: info.trim(),
      userId
    });

    if (success) {
      message.success('自定义模板添加成功');
      setTitle('');
      setInfo('');
      onClose();
    }
  };

  const handleCancel = () => {
    setTitle('');
    setInfo('');
    setCurrentTemplateIndex(0);
    setAiPrompt('');
    setIsAiGenerating(false);
    setIsFileGenerating(false);
    onClose();
  };

  const handlePrevTemplate = () => {
    setCurrentTemplateIndex((prev) => 
      prev === 0 ? EXAMPLE_TEMPLATES_LIST.length - 1 : prev - 1
    );
  };

  const handleNextTemplate = () => {
    setCurrentTemplateIndex((prev) => 
      prev === EXAMPLE_TEMPLATES_LIST.length - 1 ? 0 : prev + 1
    );
  };

  // AI 生成模板处理函数
  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      message.warning('请输入要点内容');
      return;
    }

    setIsAiGenerating(true);
    try {
      // 调用后端 /api/chat/template 接口
      const response = await ApiService.generateTemplateByKeyword({
        input: aiPrompt.trim()
      });
      
      // 检查响应状态（Coze API code=0 表示成功）
      if (response.data && response.data.code === 0 && response.data.data) {
        // 解析 data 字段（是一个 JSON 字符串）
        try {
          const parsedData = JSON.parse(response.data.data);
          const generatedTemplate = parsedData.output || response.data.data;
          setInfo(generatedTemplate);
          message.success('模板生成成功');
        } catch (parseError) {
          // 如果解析失败，直接使用原始数据
          setInfo(response.data.data);
          message.success('模板生成成功');
        }
      } else {
        // 如果有错误消息
        message.error(response.data?.msg || '生成失败，请重试');
      }
    } catch (error) {
      handleError(error, '生成模板失败', 'AddTemplateModal');
    } finally {
      setIsAiGenerating(false);
    }
  };

  // 文件学习模板处理函数
  const handleFileGenerate = async () => {
    setIsFileGenerating(true);
    try {
      // 模拟接口调用延迟
      await new Promise(resolve => setTimeout(resolve, 1500));
      // 第一阶段：返回固定的模拟模板内容
      const mockTemplate = `基于历史文件学习生成的模板内容：
1、项目背景与目标
2、核心功能模块
3、技术架构设计
4、实施计划
5、风险评估
6、资源配置
7、预期成果`;
      setInfo(mockTemplate);
      message.success('基于文件学习生成成功');
    } catch (error) {
      message.error('生成失败，请重试');
    } finally {
      setIsFileGenerating(false);
    }
  };

  const placeholderTemplate = `样例：
1、 公司概况与历史沿革
2、 商业模式与产品
3、 市场分析
4、 核心壁垒
5、 竞争对比
6、 客户订单进展
7、 未来发展战略
财务与融资`

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      width={1000}
      closable={true}
      className="notion-modal"
      styles={{
        content: { padding: 0, borderRadius: 16, overflow: 'hidden' },
        header: { display: 'none' },
        body: { padding: 0 },
      }}
      footer={null}
    >
      {/* 自定义标题栏 */}
      <div className="px-8 pt-7 pb-4">
        <h2 className="text-lg font-bold text-[#37352F]">添加自定义模板</h2>
      </div>

      <div className="flex gap-8 px-8 pb-2">
        {/* 左侧：自定义模板表单 */}
        <div className="flex-1 space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#37352F] mb-2">
              模板标题 <span className="text-red-400">*</span>
            </label>
            <input
              placeholder="请输入模板标题"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={50}
              className="w-full px-4 py-2.5 text-sm border border-[#E9E9E7] rounded-xl bg-[#FBFBFA] placeholder:text-[#B4B4B0] focus:outline-none focus:border-[#D1D1CF] focus:ring-0 transition-colors"
            />
            <div className="text-right text-[11px] text-[#B4B4B0] mt-1">{title.length} / 50</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#37352F] mb-2">
              模板内容 <span className="text-red-400">*</span>
            </label>
            <textarea
              placeholder={placeholderTemplate}
              value={info}
              onChange={(e) => { if (e.target.value.length <= 500) setInfo(e.target.value); }}
              rows={14}
              disabled={isAiGenerating || isFileGenerating}
              className="w-full px-4 py-3 text-sm border border-[#E9E9E7] rounded-xl bg-[#FBFBFA] placeholder:text-[#B4B4B0] focus:outline-none focus:border-[#D1D1CF] focus:ring-0 resize-none transition-colors disabled:opacity-50"
            />
            <div className="text-right text-[11px] text-[#B4B4B0] mt-1">{info.length} / 500</div>
          </div>

          {/* 参考建议 */}
          <div className="p-4 bg-[#F7F7F5] rounded-xl border border-[#E9E9E7]">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-[#91918E] mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
              </svg>
              <div className="text-[12px] text-[#73726E] leading-relaxed">
                <p className="font-medium text-[#37352F] mb-1">参考建议</p>
                <ul className="space-y-0.5">
                  <li>· 模板命名建议简洁明了，如"XXX访谈"、"XXX行业调研"等</li>
                  <li>· 模板内容为待输出纪要的大纲</li>
                  <li>· 创建后的模板将出现在模板选择区域</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 右侧：辅助生成模块区域 */}
        <div className="flex-1 mt-7 flex flex-col gap-5 max-h-[600px] overflow-y-auto">

          {/* AI生成区域 */}
          <div className="bg-[#F7F7F5] rounded-xl p-5 border border-[#E9E9E7]">
            <h3 className="text-center text-sm font-bold text-[#37352F] mb-4">AI 生成</h3>
            
            {/* 基于关键词学习模块 */}
            <div className="bg-white rounded-xl p-4 border border-[#E9E9E7]">
              <div className="text-sm font-medium text-[#37352F] mb-3">基于关键词学习</div>
              <div className="flex gap-2 mb-2">
                <input
                  placeholder="输入模板要点"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  disabled={isAiGenerating}
                  maxLength={200}
                  className="flex-1 px-3 py-2 text-sm border border-[#E9E9E7] rounded-lg bg-[#FBFBFA] placeholder:text-[#B4B4B0] focus:outline-none focus:border-[#D1D1CF] focus:ring-0 transition-colors disabled:opacity-50"
                />
                <button
                  onClick={handleAiGenerate}
                  disabled={isAiGenerating || isFileGenerating}
                  className="px-4 py-2 text-sm font-medium bg-[#37352F] text-white rounded-lg hover:bg-[#2F2E2B] transition-colors disabled:opacity-40 cursor-pointer whitespace-nowrap"
                >
                  {isAiGenerating ? '生成中' : '生成'}
                </button>
              </div>
              <div className="text-[11px] text-[#91918E] flex items-center gap-1">
                <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                </svg>
                输入简要要点，AI 将帮助生成模板大纲
              </div>
            </div>

            {/* 基于历史文件学习模块 */}
            {/* <div className="bg-white rounded-xl p-4 border border-[#E9E9E7] mt-3">
              <div className="text-sm font-medium text-[#37352F] mb-3">基于历史文件学习</div>
              <button
                onClick={handleFileGenerate}
                disabled={isAiGenerating || isFileGenerating}
                className="w-full px-4 py-2 text-sm border border-[#E9E9E7] rounded-lg bg-[#FBFBFA] text-[#37352F] hover:bg-[#F0F0EE] transition-colors disabled:opacity-40 cursor-pointer"
              >
                {isFileGenerating ? '学习生成中...' : '基于历史文件生成'}
              </button>
              <div className="text-[11px] text-[#91918E] mt-2 flex items-center gap-1">
                <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                </svg>
                建议输入「场景 + 目标」，如：SaaS 客户访谈、竞品调研会议等
              </div>
            </div> */}
          </div>

          {/* 案例模板模块 */}
          <div className="bg-[#F7F7F5] rounded-xl p-5 border border-[#E9E9E7]">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-[#37352F]">案例模板</span>
              <button
                onClick={() => {
                  setTitle(currentTemplate.title);
                  setInfo(currentTemplate.info);
                }}
                className="text-[11px] px-3 py-1.5 bg-white border border-[#E9E9E7] text-[#37352F] rounded-lg hover:bg-[#F0F0EE] transition-colors cursor-pointer"
              >
                复用此模板
              </button>
            </div>
            
            <div className="text-sm font-medium text-[#37352F] mb-2">{currentTemplate.title}</div>
            <div className="bg-white p-4 rounded-xl border border-[#E9E9E7] text-[12px] text-[#73726E] leading-relaxed whitespace-pre-wrap max-h-72 overflow-y-auto">
              {currentTemplate.info}
            </div>
            
            {/* 切换按钮 */}
            <div className="flex items-center justify-between mt-3">
              <span className="text-[11px] text-[#91918E]">
                {currentTemplateIndex + 1} / {EXAMPLE_TEMPLATES_LIST.length}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handlePrevTemplate}
                  className="text-[11px] px-3 py-1.5 bg-white border border-[#E9E9E7] text-[#73726E] rounded-lg hover:bg-[#F0F0EE] transition-colors cursor-pointer"
                >
                  上一个
                </button>
                <button
                  onClick={handleNextTemplate}
                  className="text-[11px] px-3 py-1.5 bg-white border border-[#E9E9E7] text-[#73726E] rounded-lg hover:bg-[#F0F0EE] transition-colors cursor-pointer"
                >
                  下一个
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 底部操作栏 */}
      <div className="flex justify-end gap-3 px-8 py-5 border-t border-[#E9E9E7]">
        <button
          onClick={handleCancel}
          className="px-6 py-2.5 text-sm text-[#73726E] bg-white border border-[#E9E9E7] rounded-xl hover:bg-[#F7F7F5] transition-colors cursor-pointer"
        >
          取消
        </button>
        <button
          onClick={handleSubmit}
          disabled={addLoading}
          className="px-6 py-2.5 text-sm font-medium text-white bg-[#37352F] rounded-xl hover:bg-[#2F2E2B] transition-colors disabled:opacity-40 cursor-pointer"
        >
          {addLoading ? '添加中...' : '添加模板'}
        </button>
      </div>
    </Modal>
  );
};

export default AddTemplateModal;
